import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import {
  ScorecardType,
  StudentWithCount,
  StudentWithAttendance,
  ReviewEnvelope,
  ReviewMeetingStatusInfo,
} from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewScorecardsQuery');

export const QUERY_KEY = 'reviewScorecards';

export type ReviewScorecardsMeeting = ReviewMeetingStatusInfo & {
  // These are (name, score) pairs for praised, warned, and absent students.
  scorecards: Record<ScorecardType, StudentWithCount[] | StudentWithAttendance[]>;
};

export type ReviewScorecardsResponse = ReviewEnvelope<ReviewScorecardsMeeting>;

export function useReviewScorecards({ date, periodId }: { date: Date; periodId: string }) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status } = useQuery<null | ReviewScorecardsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review scorecards', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const response = await httpClient.get<ReviewScorecardsResponse>(`/review/getScorecards?${paramsQueryStr}`);
      log.info('Fetched review scorecards successfully', {
        meetingCount: response.meetings.length,
        dayStatus: response.status,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review scorecards', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status };
}
